import { ApplicationConfig, importProvidersFrom, isDevMode, provideZoneChangeDetection } from '@angular/core';
import { NavigationError, provideRouter, TitleStrategy, withNavigationErrorHandler } from '@angular/router';
import { routes } from './app.routes';
import { SpendkeyTitleStrategy } from './Shared/spendkey-title-strategy';
import { provideAnimations } from '@angular/platform-browser/animations';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withFetch, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { EncryptDecryptService } from './Services/encrypt-decrypt.service';
import { authTokenInterceptor } from './Shared/Interceptor/auth-token.interceptor';
import { UK_DATE_FORMATS } from './Shared/uk-date-formats';
import { MaterialModule } from './Shared/MaterialModule/material/material.module';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { OktaAuthModule, OKTA_CONFIG } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { environment } from 'src/environments/environment';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { DEFAULT_LANG } from './Shared/constants/supported-languages';
import { TitleCaseMissingTranslationHandler } from './Shared/i18n/title-case-missing-translation.handler';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideRbacSodFeature } from './Store/rbac-sod/rbac-sod.providers';

export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, 'assets/i18n/', '.json');
}

/**
 * Recovers from stale-bundle chunk failures (Bug #9912).
 *
 * After a redeploy, browsers still running the previous index.html request
 * lazy-route chunks whose hashed filenames no longer exist. The dynamic
 * import rejects, the navigation errors, and the wildcard route renders
 * LoginComponent — whose init clears localStorage, force-logging the user
 * out mid-session. A full page load onto the target URL fetches the fresh
 * index.html + matching chunks and preserves the session.
 *
 * The sessionStorage timestamp guards against a reload loop when a chunk is
 * genuinely unfetchable (offline, CDN outage): one reload per URL per 10s.
 */
export function staleChunkReloadHandler(error: NavigationError): void {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return;
  }
  const message = String((error.error as Error)?.message ?? error.error ?? '');
  const isChunkFailure = /Failed to fetch dynamically imported module|Importing a module script failed|ChunkLoadError/i.test(message);
  if (!isChunkFailure) {
    return;
  }
  const retryKey = `sk-chunk-reload:${error.url}`;
  const lastTry = Number(sessionStorage.getItem(retryKey) ?? 0);
  if (Date.now() - lastTry < 10_000) {
    return;
  }
  sessionStorage.setItem(retryKey, String(Date.now()));
  window.location.assign(error.url);
}


export function loggerCallback(_logLevel: LogLevel, _message: string) {
  // console.log(message);
}

export function oktaConfigFactory(): { oktaAuth: OktaAuth } {
  const config = window.location.host === 'momentumgroup.spendkey.app'
    ? (environment as any).MomentumOktaAuth
    : environment.OktaAuth;
  return { oktaAuth: new OktaAuth(config) };
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.clientId,
      authority: environment.msalConfig.tenant,
      redirectUri: '/',
      // postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  // Only protect Microsoft Graph API calls — not the Spendkey backend or login page requests.
  // Previously '/' was used which matches every URL, causing the MsalInterceptor to trigger
  // a Microsoft login redirect for unauthenticated HTTP calls on the login page.
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', environment.msalConfig.consentScopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.msalConfig.consentScopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(
      { eventCoalescing: true }
    ),
    provideHttpClient(
      withFetch(),
      withInterceptors([authTokenInterceptor]),
      withInterceptorsFromDi()
    ),
    provideRouter(routes, withNavigationErrorHandler(staleChunkReloadHandler)),
    // App-scope tab title. Runs on every NavigationEnd regardless of which
    // components are mounted — kills the "SpendkeyV18" flash from index.html
    // and the missed-first-NavigationEnd race the old HeaderComponent impl had.
    { provide: TitleStrategy, useClass: SpendkeyTitleStrategy },
    provideAnimations(),
    // NgRx root store (US #9320 / F2 #9345 — first NgRx use in the app).
    // Root reducer map is intentionally empty: the admin-console feature slices
    // (audit, workflow) register lazily via provideState()/provideEffects()
    // at the admin-console route (F4 #9347), so their state is loaded on demand.
    provideStore(
      {},
      {
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: true,
          strictActionSerializability: true,
        },
      }
    ),
    provideEffects(),
    // RBAC + SoD slice promoted to root (#9749): feature surfaces (variance,
    // savings-tracker, KPI) gate write affordance off MePermissions, so the
    // slice must be available before the user enters the admin console.
    provideRbacSodFeature(),
    // DevTools in dev builds only. `logOnly` merely disables time-travel/dispatch
    // from the Redux DevTools extension — the extension still subscribes and can
    // read every action + state slice (incl. admin-console permissions, role
    // assignments, SoD rules, audit rows, user identities) live. Gating on
    // isDevMode() keeps it fully functional locally and tree-shakes the import
    // out of production bundles entirely.
    ...(isDevMode()
      ? [provideStoreDevtools({ maxAge: 25, connectInZone: true })]
      : []),
    EncryptDecryptService,
    importProvidersFrom(
      MaterialModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
        missingTranslationHandler: {
          provide: MissingTranslationHandler,
          useClass: TitleCaseMissingTranslationHandler,
        },
        useDefaultLang: true,
        defaultLanguage: DEFAULT_LANG,
      })
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    importProvidersFrom(OktaAuthModule),
    { provide: OKTA_CONFIG, useFactory: oktaConfigFactory },
    provideCharts(withDefaultRegisterables()),
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS] },
    { provide: MAT_DATE_FORMATS, useValue: UK_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
  ],
};
